
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted,
  getCurrentInstance,
  watch,
  nextTick
} from 'vue';
import { fetchEmployers } from '@/services/airtable';
import { debounce } from 'lodash';

export default defineComponent({
  name: 'EmployerBrowser',
  props: {
    isSponsor: {
      type: Boolean,
      default: false
    }
  },
  emits: ['program-created', 'save-for-later', 'search-employers'],
  setup(props, { emit }) {
    // Reactive data
    const searchQuery = ref('');
    const searchResults = ref([]);
    const selectedEmployers = ref([]);
    const searchLoading = ref(false);
    const savedEmployers = ref(new Set());
    const showingSaved = ref(JSON.parse(localStorage.getItem('showingSavedFilter')) || false);
    const currentPage = ref(1);
    const pageSize = ref(50);
    const hasMoreResults = ref(true);
    const totalRecords = ref(0);
    const totalPages = ref(0);
    const nextOffset = ref(null);
    const initialLoadCompleted = ref(false);
    const loadingInProgress = ref(false);
    const loadingMore = ref(false); // New ref for loading more

    const instance = getCurrentInstance();
    const root = instance?.proxy;

    // Table headers
    const headers = [
      {
        text: 'Name',
        value: 'name',
        sortable: false,
        align: 'left',
        width: '20%'
      },
      {
        text: 'Industry',
        value: 'industry',
        sortable: false,
        align: 'left',
        width: '15%'
      },
      {
        text: 'Website',
        value: 'website',
        sortable: false,
        align: 'center',
        width: '8%'
      },
      {
        text: 'Description',
        value: 'shortDescription',
        sortable: false,
        align: 'left',
        width: '25%'
      },
      {
        text: 'Jurisdiction',
        value: 'jurisdiction',
        sortable: false,
        align: 'center',
        width: '12%'
      },
      {
        text: 'Pathways',
        value: 'pathways',
        sortable: false,
        align: 'center',
        width: '10%'
      },
      {
        text: 'Select',
        value: 'createProgram',
        sortable: false,
        align: 'center',
        width: '5%'
      },
      {
        text: 'Save',
        value: 'saveForLater',
        sortable: false,
        align: 'center',
        width: '5%'
      }
    ];

    // Computed properties
    // Feature flag: toggle 'Saved' bookmarking functionality
    const savedFeatureEnabled = false;

    const filteredResults = computed(() => {
      const results = searchResults.value || [];
      const validResults = results.filter(
        employer => employer && typeof employer === 'object' && employer.id
      );

      if (!showingSaved.value) {
        return validResults;
      }

      return validResults.filter(employer => savedEmployers.value.has(employer.id));
    });

    // Items array including optional loader row
    const displayItems = computed(() => {
      if (loadingMore.value) {
        return [...filteredResults.value, { id: '__loader', isLoader: true }];
      }
      return filteredResults.value;
    });

    // Computed placeholder total for Vuetify pagination when real total is unknown
    const serverItemCount = computed(() => {
      if (totalRecords.value && totalRecords.value > 0) {
        return totalRecords.value;
      }
      // If we don't know the total yet but Airtable said there are more pages,
      // return current loaded records + 1 so Vuetify enables the next-page button
      if (hasMoreResults.value) {
        return currentPage.value * pageSize.value + 1;
      }
      return searchResults.value.length;
    });

    // Computed virtual height for v-virtual-scroll
    const virtualHeight = computed(() => {
      if (typeof window !== 'undefined') {
        return window.innerHeight * 0.63;
      }
      return 600; // Fallback height
    });

    // Debounced search
    const debouncedSearch = debounce(async query => {
      // Prevent multiple simultaneous calls
      if (loadingInProgress.value) {
        return;
      }

      loadingInProgress.value = true;
      clearCache(); // Clear cache when search changes
      searchLoading.value = true;

      try {
        const response = await fetchEmployers({
          searchQuery: query,
          pageSize: pageSize.value,
          offset: null,
          includeTotal: true,
          sort: currentSortParam()
        });
        searchResults.value = response.records;
        hasMoreResults.value = response.hasMore;
        nextOffset.value = response.nextOffset;
        totalRecords.value = response.total || 0;
        totalPages.value = Math.ceil(totalRecords.value / pageSize.value);
        currentPage.value = 1;
      } catch (error) {
        console.error('Search failed:', error);
        hasMoreResults.value = false;
        totalRecords.value = 0;
        totalPages.value = 0;
      } finally {
        searchLoading.value = false;
        loadingInProgress.value = false;
      }
    }, 300);

    // Data loading functions
    async function loadEmployers(isLoadingMore = false, page = null) {
      if (loadingInProgress.value) {
        return; // Prevent concurrent requests
      }

      loadingInProgress.value = true;

      if (!isLoadingMore) {
        clearCache(); // Clear cache when loading fresh data
        searchResults.value = [];
        currentPage.value = page || 1;
        nextOffset.value = null;
        hasMoreResults.value = true;
      }

      if (isLoadingMore) {
        loadingMore.value = true;
      } else {
        searchLoading.value = true;
      }

      try {
        const response = await fetchEmployers({
          searchQuery: searchQuery.value,
          pageSize: pageSize.value,
          offset: nextOffset.value,
          includeTotal: !isLoadingMore,
          sort: currentSortParam()
        });

        hasMoreResults.value = response.hasMore;
        nextOffset.value = response.nextOffset;

        if (response.total !== undefined) {
          totalRecords.value = response.total;
          totalPages.value = Math.ceil(totalRecords.value / pageSize.value);
        }

        if (isLoadingMore) {
          searchResults.value = [...searchResults.value, ...response.records];
          currentPage.value += 1;
        } else {
          searchResults.value = response.records;
        }
      } catch (error) {
        console.error('Failed to load employers:', error);
        hasMoreResults.value = false;
        if (!isLoadingMore) {
          totalRecords.value = 0;
          totalPages.value = 0;
        }
      } finally {
        if (isLoadingMore) {
          loadingMore.value = false;
        } else {
          searchLoading.value = false;
        }
        loadingInProgress.value = false;
      }
    }

    // Search function
    async function searchEmployers() {
      await loadEmployers();
      emit('search-employers', searchQuery.value);
    }

    // Cache for loaded data to avoid re-fetching
    const dataCache = ref(new Map());
    const cacheKey = computed(() => `${searchQuery.value}_${pageSize.value}`);

    // Load data for a specific page with caching and optimization
    async function loadPageData(page) {
      // Prevent multiple simultaneous calls
      if (loadingInProgress.value) {
        return;
      }

      loadingInProgress.value = true;
      searchLoading.value = true;

      try {
        const cache = dataCache.value.get(cacheKey.value);
        const targetRecords = page * pageSize.value;

        // Check if we have enough cached data
        if (cache && cache.allRecords.length >= targetRecords) {
          // Use cached data
          const startIndex = (page - 1) * pageSize.value;
          const endIndex = startIndex + pageSize.value;
          searchResults.value = cache.allRecords.slice(startIndex, endIndex);
          hasMoreResults.value = cache.hasMore || cache.allRecords.length > endIndex;

          if (cache.total !== undefined) {
            totalRecords.value = cache.total;
            totalPages.value = Math.ceil(totalRecords.value / pageSize.value);
          }

          searchLoading.value = false;
          loadingInProgress.value = false;
          return;
        }

        // Load fresh data if cache is insufficient
        let allRecords = cache ? [...cache.allRecords] : [];
        let currentOffset = cache ? cache.nextOffset : null;
        let hasMore = cache ? cache.hasMore : true;
        let totalFromAPI = cache ? cache.total : undefined;

        // Only load additional pages if we need more data
        while (allRecords.length < targetRecords && hasMore) {
          const response = await fetchEmployers({
            searchQuery: searchQuery.value,
            pageSize: pageSize.value,
            offset: currentOffset,
            includeTotal: allRecords.length === 0 && !cache // Only get total on first request
          });

          if (!response.records || response.records.length === 0) {
            hasMore = false;
            break;
          }

          allRecords = [...allRecords, ...response.records];
          hasMore = response.hasMore;
          currentOffset = response.nextOffset;

          if (response.total !== undefined) {
            totalFromAPI = response.total;
          }
        }

        // Update cache
        dataCache.value.set(cacheKey.value, {
          allRecords,
          hasMore,
          nextOffset: currentOffset,
          total: totalFromAPI
        });

        // Show only the records for the current page
        const startIndex = (page - 1) * pageSize.value;
        const endIndex = startIndex + pageSize.value;
        searchResults.value = allRecords.slice(startIndex, endIndex);
        hasMoreResults.value = hasMore || allRecords.length > endIndex;

        if (totalFromAPI !== undefined) {
          totalRecords.value = totalFromAPI;
          totalPages.value = Math.ceil(totalRecords.value / pageSize.value);
        } else if (!hasMore) {
          // We have loaded all records but did not request total; use loaded count
          totalRecords.value = allRecords.length;
          totalPages.value = Math.ceil(totalRecords.value / pageSize.value);
        }
      } catch (error) {
        console.error('Failed to load page data:', error);
        // Don't clear existing data on error, just show a message
        if (searchResults.value.length === 0) {
          totalRecords.value = 0;
          totalPages.value = 0;
        }
      } finally {
        searchLoading.value = false;
        loadingInProgress.value = false;
      }
    }

    // Clear cache when search query changes
    function clearCache() {
      dataCache.value.clear();
    }

    // Enhanced pagination handlers
    async function handlePageChange(page) {
      if (page === currentPage.value) return; // Don't reload if same page
      currentPage.value = page;
      await loadPageData(page);
    }

    // Scroll handler for virtual scroll
    function onScroll(event) {
      const el = event.target;
      if (!el) return;
      // Trigger when near bottom (200px)
      const threshold = 200;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < threshold &&
        hasMoreResults.value &&
        !loadingInProgress.value
      ) {
        loadEmployers(true);
      }
    }

    async function handleItemsPerPageChange(itemsPerPage) {
      if (itemsPerPage === pageSize.value) return; // Don't reload if same page size
      clearCache(); // Clear cache when page size changes
      pageSize.value = itemsPerPage;
      currentPage.value = 1;
      await loadPageData(1);
    }

    // Program creation functions
    async function createProgram(employer) {
      if (!employer || !employer?.website || employer.website?.trim().length === 0) {
        emit('program-created', null);
        return;
      }

      let validatedUrl;
      try {
        const website = employer?.website;
        if (!website) {
          emit('program-created', null);
          return;
        }
        validatedUrl = new URL(website.startsWith('http') ? website : `https://${website}`);

        await nextTick();
        emit('program-created', validatedUrl.href);
      } catch (e) {
        emit('program-created', null);
      }
    }

    async function createMultiplePrograms() {
      if (selectedEmployers.value.length === 0) return;

      const websites = selectedEmployers.value
        .map(employer => {
          if (!employer?.website) return null;
          try {
            const website = employer?.website;
            if (!website) return null;
            return new URL(website.startsWith('http') ? website : `https://${website}`).href;
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean)
        .join(', ');

      if (websites) {
        await nextTick();
        emit('program-created', websites);
      }
    }

    // Save/bookmark functions
    function persistSavedEmployers() {
      localStorage.setItem('savedEmployers', JSON.stringify([...savedEmployers.value]));
    }

    function saveForLater(employer) {
      if (!employer?.id) return;
      if (savedEmployers.value.has(employer.id)) {
        savedEmployers.value.delete(employer.id);
      } else {
        savedEmployers.value.add(employer.id);
      }
      persistSavedEmployers();
      emit('save-for-later', employer);
    }

    function loadSavedEmployers() {
      const saved = localStorage.getItem('savedEmployers');
      if (saved) {
        savedEmployers.value = new Set(JSON.parse(saved));
      }
    }

    function toggleSavedFilter() {
      showingSaved.value = !showingSaved.value;
      localStorage.setItem('showingSavedFilter', JSON.stringify(showingSaved.value));
    }

    // Sorting logic
    const sortField = ref(null);
    const sortDirection = ref('asc');
    const hasUserChosenSort = ref(false);

    function sortIcon(column) {
      if (sortField.value === column) {
        return sortDirection.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
      }
      return 'mdi-arrow-up'; // Default to ascending for other columns
    }

    async function changeSort(column) {
      if (sortField.value === column) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortField.value = column;
        sortDirection.value = 'asc';
      }
      hasUserChosenSort.value = true; // user explicitly sorted
      // Reset list & cache and load first page with new sorting
      clearCache();
      searchResults.value = [];
      nextOffset.value = null;
      hasMoreResults.value = true;
      await loadEmployers(false);
    }

    // Intersection Observer for infinite scroll
    function handleIntersect(entries) {
      if (entries[0].isIntersecting && hasMoreResults.value) {
        loadEmployers(true);
      }
    }

    // Watch for search query changes - only trigger if there's actually a query
    watch(searchQuery, (newQuery, oldQuery) => {
      // Skip initial setup where both values might be empty/undefined
      if (oldQuery === undefined && (!newQuery || newQuery.trim().length === 0)) {
        return;
      }

      // Skip if loading is in progress
      if (loadingInProgress.value) {
        return;
      }

      // Only search if query has actually changed and has meaningful content
      if (newQuery !== oldQuery && newQuery && newQuery.trim().length > 2) {
        debouncedSearch(newQuery.trim());
      } else if (newQuery === '' && oldQuery && oldQuery.length > 0 && initialLoadCompleted.value) {
        // Only reload initial data if we had previously loaded initial data
        // and the user explicitly cleared the search
        clearCache();
        initialLoadCompleted.value = false; // Reset to allow reload
        loadInitialData();
      }
    });

    // Separate function for initial data loading
    async function loadInitialData() {
      // Prevent multiple simultaneous calls
      if (loadingInProgress.value || initialLoadCompleted.value) {
        return;
      }

      loadingInProgress.value = true;
      clearCache(); // Clear cache when loading initial data
      searchLoading.value = true;

      try {
        // Capture route info once to avoid reactive dependencies
        const routePath = root?.$route?.path || '';
        const shareCode = root?.$route?.params?.shareCode;
        const isSponsorRoute = routePath.startsWith('/sponsor/') && shareCode;

        // NOTE: We pass includeTotal: false here to avoid fetching ALL records which triggers many API requests
        const response = await fetchEmployers({
          pageSize: pageSize.value,
          includeTotal: false,
          sort: currentSortParam()
        });

        searchResults.value = response.records || [];
        hasMoreResults.value = response.hasMore;
        nextOffset.value = response.nextOffset;
        // We don't know the real total yet; keep totalRecords at 0 so display uses loaded count
        totalRecords.value = 0;
        totalPages.value = 0;

        // Handle sponsor route filtering only once
        if (isSponsorRoute && searchResults.value?.length > 0) {
          const programSettings = localStorage.getItem('programSettings');
          if (programSettings) {
            try {
              const { employerDomains, includeSelfCreationOfPrograms } =
                JSON.parse(programSettings);
              if (employerDomains?.length > 0 && includeSelfCreationOfPrograms) {
                searchResults.value = (searchResults.value || []).filter(
                  result => result?.id && employerDomains.includes(result.id)
                );
                totalRecords.value = searchResults.value.length;
                totalPages.value = Math.ceil(totalRecords.value / pageSize.value);
              }
            } catch (parseError) {
              console.warn('Failed to parse programSettings:', parseError);
            }
          }
        }

        initialLoadCompleted.value = true;
      } catch (error) {
        console.error('Failed to load initial employers:', error);
        hasMoreResults.value = false;
        totalRecords.value = 0;
        totalPages.value = 0;
      } finally {
        searchLoading.value = false;
        loadingInProgress.value = false;
      }
    }

    // Initial data loading - only call once on mount
    onMounted(async () => {
      // Lock body scroll
      if (typeof document !== 'undefined') {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }

      // Load saved employers first (synchronous)
      loadSavedEmployers();

      // Only load initial data if not already loading or completed
      if (!loadingInProgress.value && !initialLoadCompleted.value) {
        await loadInitialData();
      }

      // Set up Intersection Observer for sentinel
      // No explicit observer setup needed; handled by v-intersect directive
    });

    onUnmounted(() => {
      // Restore body scroll
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    });

    // Computed property for display total
    const displayTotal = computed(() => {
      if (totalRecords.value > 0) {
        return `${totalRecords.value.toLocaleString()} Total Records Found`;
      }
      const plus = hasMoreResults.value ? '+' : '';
      return `${searchResults.value.length.toLocaleString()}${plus} Records Loaded`;
    });

    // Computed property to show sort icons only when user has explicitly sorted or no query
    const showSortIcons = computed(() => {
      return hasUserChosenSort.value || searchQuery.value.trim().length === 0;
    });

    // Helper function to compute sort params
    const currentSortParam = () => {
      if (!sortField.value) return [];
      return [
        {
          field:
            sortField.value === 'name'
              ? 'Company Name'
              : sortField.value === 'industry'
              ? 'Industry'
              : 'Company Name',
          direction: sortDirection.value
        }
      ];
    };

    return {
      // Reactive data
      searchQuery,
      searchResults,
      selectedEmployers,
      searchLoading,
      savedEmployers,
      showingSaved,
      currentPage,
      pageSize,
      hasMoreResults,
      totalRecords,
      totalPages,
      headers,
      filteredResults,
      dataCache,
      cacheKey,
      initialLoadCompleted,
      loadingInProgress,
      loadingMore, // Expose loadingMore for template binding
      serverItemCount,
      virtualHeight,
      displayItems,

      // Functions
      searchEmployers,
      loadEmployers,
      loadInitialData,
      handlePageChange,
      handleItemsPerPageChange,
      loadPageData,
      createProgram,
      createMultiplePrograms,
      saveForLater,
      toggleSavedFilter,
      clearCache,
      sortField, // Expose sortField for template binding
      sortDirection, // Expose sortDirection for template binding
      sortIcon, // Expose sortIcon for template binding
      changeSort, // Expose changeSort for template binding
      handleIntersect, // Expose handleIntersect for template binding
      onScroll, // expose scroll handler
      displayTotal, // Expose displayTotal for template binding
      showSortIcons, // Expose showSortIcons for template binding
      savedFeatureEnabled // Expose savedFeatureEnabled for template binding
    };
  }
});
